/**
 * Authentication Context
 * Manages user authentication state, login, logout, and token management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use relative URLs - requests are proxied via setupProxy.js in development
// In production, set REACT_APP_API_URL to the actual backend URL
const API_URL = process.env.REACT_APP_API_URL || '';

// Axios instance with interceptors
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tpd_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('tpd_token');
      const savedUser = localStorage.getItem('tpd_user');
      
      if (token && savedUser) {
        try {
          // Verify token with backend
          const response = await api.get('/api/auth/me');
          setUser(response.data);
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('tpd_token');
          localStorage.removeItem('tpd_user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password, otpCode = null) => {
    setError(null);
    
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
        otp_code: otpCode
      });

      const { access_token, user: userData, requires_mfa, session_id } = response.data;

      // If MFA is required, return the session_id for OTP verification
      if (requires_mfa) {
        return { requires_mfa: true, session_id };
      }

      // Store token and user data
      localStorage.setItem('tpd_token', access_token);
      localStorage.setItem('tpd_user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Verify MFA
  const verifyMFA = useCallback(async (sessionId, otpCode) => {
    setError(null);
    
    try {
      const response = await api.post('/api/auth/verify-mfa', {
        session_id: sessionId,
        otp_code: otpCode
      });

      const { access_token, user: userData } = response.data;

      localStorage.setItem('tpd_token', access_token);
      localStorage.setItem('tpd_user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Verification failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('tpd_token');
      localStorage.removeItem('tpd_user');
      setUser(null);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    loading,
    error,
    login,
    verifyMFA,
    logout,
    clearError,
    isAdmin,
    api
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
